import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { ApiMeta } from '@/lib/http-core';
import { downloadViaLink, exportJobsPollInterval } from '@/lib/exportJobs';
import { toOrderExportError, type OrderExportError } from './lib';
import type { OrderExportCriteria, OrderExportJob } from './types';

/** 주문 엑셀 다운로드 요청 API(gateway /api/admin/v1/orders/exports/** 통과). generated OpenAPI 밖이라 adminFetch. */
const BASE = '/api/admin/v1/orders/exports';

export { EXPORT_JOBS_POLL_MS, exportJobsPollInterval } from '@/lib/exportJobs';

export const orderExportJobsKey = ['admin', 'order-export-jobs'] as const;

export interface OrderExportJobsPage {
  items: OrderExportJob[];
  meta: ApiMeta | null;
}

const normalize = (j: OrderExportJob): OrderExportJob => ({ ...j, files: j.files ?? [] });

/** 내 요청 목록(최신순). PENDING·RUNNING 이 있으면 4초마다 다시 불러오고, 모두 끝나면 멈춘다. */
export const useOrderExportJobsQuery = (page: number, size: number) =>
  useQuery<OrderExportJobsPage, OrderExportError>({
    queryKey: [...orderExportJobsKey, page, size],
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?page=${Math.max(0, page - 1)}&size=${size}`);
      if (!res.ok) throw await toOrderExportError(res);
      const env = (await res.json()) as { data: OrderExportJob[] | null; meta?: ApiMeta | null };
      return { items: (env.data ?? []).map(normalize), meta: env.meta ?? null };
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) => exportJobsPollInterval(query.state.data?.items),
  });

/** 다운로드 요청(202). 같은 Idempotency-Key 로 다시 보내면 BE 가 같은 요청을 돌려준다(두 번 눌러도 1건). */
export const useRequestOrderExportMutation = () => {
  const qc = useQueryClient();
  return useMutation<OrderExportJob, OrderExportError, { criteria: OrderExportCriteria; idempotencyKey: string }>({
    mutationFn: async ({ criteria, idempotencyKey }) => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(criteria),
      });
      if (!res.ok) throw await toOrderExportError(res);
      const env = (await res.json()) as { data: OrderExportJob };
      return normalize(env.data);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: orderExportJobsKey }),
  });
};

export const useDeleteOrderExportJobMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, OrderExportError, string>({
    mutationFn: async (exportJobId) => {
      const res = await adminFetch(`${BASE}/${encodeURIComponent(exportJobId)}`, { method: 'DELETE' });
      if (!res.ok) throw await toOrderExportError(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: orderExportJobsKey }),
  });
};

/** 파일 하나 받기 — 링크를 새로 받아 S3 직접(QA·운영) 또는 blob(로컬). 공용 downloadViaLink. */
export const downloadOrderExportFile = (exportJobId: string, fileNo: number, fileName: string): Promise<void> =>
  downloadViaLink(`${BASE}/${encodeURIComponent(exportJobId)}/files/${fileNo}/download-url`, fileName, toOrderExportError);
